import React, {
	useState,
	useEffect,
	useCallback,
	useMemo,
	ReactNode,
} from 'react';
import {
	notification,
	Form,
	Typography,
	Space,
	Flex,
} from 'antd';
import {
	EditOutlined,
	DeleteOutlined,
	CheckOutlined,
	CloseOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import {
	ColumnGroupType,
	ColumnType,
} from 'antd/lib/table/interface';
import { FormInstance } from 'antd/lib/form/hooks/useForm';
import type { RenderedCell } from 'rc-table/lib/interface';
import { NULL_STRING } from '@shared/utils/constants';
import { stringToValidNumericString } from '@shared/utils/string';
import Table, { TableProps } from '@client/components/Table/Table';
import showErrorNotification from '@client/utils/showErrorNotification';
import Button from '@client/components/Button';
import {
	EditableCell,
	EditableExtraProps,
} from '@client/components/EditableTable/EditableCell';
import styles from '../styles/EditableTable.module.css';
import AddButton from '../AddButton';

export type EditableColumn<Row extends object, KeyDataIndex extends keyof Row> = {
	editable?: boolean | ((row?: Row) => boolean);
	editingProps?: {
		defaultValue?: any;
	} & EditableExtraProps<Row>;
	addToFormOverride?: boolean;
	render?: (
		value: any,
		record: Row,
		index: number,
		editingRow: Row[KeyDataIndex] | null | typeof NULL_STRING
	) => React.ReactNode | RenderedCell<Row>;
} & Omit<ColumnType<Row>, 'render'>;

export type EditableTableProps<Row extends object, KeyDataIndex extends keyof Row> = {
	keyDataIndex: KeyDataIndex;
	columns: Array<(
		| ColumnGroupType<Row> |
		EditableColumn<Row, KeyDataIndex>
	)>;
	dataSource: Array<Row>;
	onSave: (id: Row[KeyDataIndex], newRow: Row) => void;
	onDelete?: (id: Row[KeyDataIndex], row: Row) => void;
	editingRow?: Row[KeyDataIndex] | null | typeof NULL_STRING;
	onEditingRowChange?: (id: Row[KeyDataIndex] | null, row?: Row) => void;
	// It really can be any
	onEdit?: (editValues: any) => void;
	editLabel?: string;
	rowClassName?: string | ((r: Row) => string);
	showActions?: boolean;
	enableDelete?: ((row: Row) => boolean) | boolean;
	enableEdit?: ((row: Row) => boolean) | boolean;
	disableEdit?: ((row: Row) => boolean);
	onAddNewChange?: (row: Row) => void;
	allowAddNew?: boolean;
	addNewText?: string | ReactNode;
	getCustomActions?: (row: Row) => ReactNode;
	getActionsBetweenEditAndDelete?: (row: Row) => ReactNode;
	iconButtons?: boolean;
	actionsTitle?: string;
	onAddNew?: (newRow: Row) => void;
	onRowClick?: (row: Row) => void;
	editingRowClassName?: string;
	form?: FormInstance<Row>;
	addButtonInHeader?: boolean;
	extraOnCancelEdit?: () => void;
	deleteMessage?:
		| string |
		ReactNode |
		((row: Row) => string | ReactNode);
	confirmTitle?: string | ReactNode;
	saveOnEnter?: boolean;
	allowEditRow?: (record: Row) => boolean;
	addNewContainerClassName?: string;
	addRowExtraContent?: ReactNode;
	addButtonTestId?: string;
	transformers?: Array<{ field: keyof Row; fn: (row: Row, changed: Partial<Row>) => any }>
	newRowOnTop?: boolean;
	addNewOverride?: Row;
	autoFocusOnAdd?: boolean;
} & Omit<TableProps<Row>, 'columns'>;

const EditableTable = <Row extends object, KeyDataIndex extends keyof Row>({
	keyDataIndex,
	columns,
	dataSource,
	editLabel = 'Edit',
	rowClassName = undefined,
	showActions = true,
	enableDelete = () => false,
	deleteMessage = undefined,
	enableEdit = () => true,
	disableEdit = () => false,
	allowAddNew = false,
	addNewText = '',
	addNewContainerClassName = '',
	addRowExtraContent,
	getCustomActions = undefined,
	getActionsBetweenEditAndDelete = undefined,
	iconButtons = false,
	actionsTitle = 'Actions',
	saveOnEnter = true,
	confirmTitle = undefined,
	transformers,
	onSave,
	onAddNew = undefined,
	onDelete,
	onRowClick = undefined,
	onAddNewChange,
	editingRow: editingRowProp,
	onEditingRowChange,
	onEdit,
	editingRowClassName = undefined,
	form: formProp = undefined,
	addButtonInHeader = true,
	extraOnCancelEdit,
	allowEditRow,
	addButtonTestId,
	newRowOnTop = false,
	addNewOverride,
	autoFocusOnAdd,
	...props
}: EditableTableProps<Row, KeyDataIndex>) => {
	const [loading, setLoading] = useState(false);
	const [form] = Form.useForm(formProp);
	const [data, setData] = useState(dataSource);
	const [editingRowState, setEditingRow] = useState(null);
	const [addingNew, setAddingNew] = useState(false);
	const [focusNewRow, setFocusNewRow] = useState(false);
	const [currentFormValues, setCurrentFormValues] = useState<Partial<Row>>({});
	const controlled = editingRowProp !== undefined;

	const editingRow = controlled ? editingRowProp : editingRowState;
	const updateEditingRow = controlled ? onEditingRowChange : setEditingRow;

	useEffect(() => {
		if (!addNewOverride) {
			return;
		}

		if (!editingRow) {
			addNew();
		}

		form.setFieldsValue(addNewOverride);
	}, [addNewOverride]);

	useEffect(() => {
		let newData = [...dataSource];

		// If we're currently creating a new item, keep the row!
		if (editingRow === NULL_STRING) {
			const newRow = data.find((row) => {
				const key = row[keyDataIndex] as Row[KeyDataIndex] | typeof NULL_STRING;

				return key === NULL_STRING;
			});

			if (newRow != null) {
				newData = newRowOnTop ? [
					newRow,
					...dataSource,
				] : [
					...dataSource,
					newRow,
				];
			}
		}

		setData(newData);

		// If we're editing a row that no longer exists, reset edit state
		if (
			editingRow != null &&
			updateEditingRow != null &&
			editingRow !== NULL_STRING &&
			!dataSource.map((r) => r[keyDataIndex]).includes(editingRow) &&
			updateEditingRow != null
		) {
			updateEditingRow(null);
		}
	}, [dataSource]);

	const startEdit = useCallback(
		(record: Row, e: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => {
			if (updateEditingRow == null) {
				return;
			}

			form.setFieldsValue({ ...record });
			setCurrentFormValues({ ...record });

			const fn = updateEditingRow as typeof onEditingRowChange;

			if (fn != null) {
				fn(record[keyDataIndex], record);
			}

			e?.stopPropagation();
		}, [form, keyDataIndex, updateEditingRow],
	);

	const checkIsEditable = useCallback((
		column: EditableTableProps<Row, KeyDataIndex>['columns'][number],
		row?: Row,
	): column is EditableColumn<Row, KeyDataIndex> => {
		const columnAsEditable = column as EditableColumn<Row, KeyDataIndex>;

		if (typeof columnAsEditable.editable === 'function') {
			return columnAsEditable.editable(row);
		}

		return columnAsEditable.editable ?? false;
	}, []);

	const addNew = (e?: unknown) => {
		setAddingNew(true);
		setFocusNewRow(e != null);

		// Add a new row with a null id and `null` in all the editable columns
		const fields = columns
			.filter((c) => {
				return checkIsEditable(c) || ('addToFormOverride' in c && c.addToFormOverride);
			})
			.reduce<Row>((obj, column) => {
				const editableColumn = column as EditableColumn<Row, KeyDataIndex>;

				return ({
					...obj,
					[editableColumn.dataIndex as string]: editableColumn.editingProps?.defaultValue || null,
				});
			}, {} as Row);

		const newRow: Row = {
			[keyDataIndex]: NULL_STRING,
			...fields,
		};

		// @ts-ignore
		form.setFieldsValue(fields);
		setCurrentFormValues(newRow);

		const newData = newRowOnTop ? [
			newRow,
			...data,
		] : [
			...data,
			newRow,
		];

		setData(newData);

		// @ts-ignore
		updateEditingRow(newRow[keyDataIndex], newRow);
		onAddNewChange?.(newRow);
	};

	useEffect(() => {
		const value = Object.values(currentFormValues).length === 0 ? 
			null : 
			currentFormValues;
		onEdit?.(value);
	}, [currentFormValues]);

	useEffect(() => {
		// If a new row has been added (not yet saved)
		// and the user cancels the editing operation,
		// the newly added row should be removed
		const rowWithNullStringKey = data.find((row) => {
			const key = row[keyDataIndex] as Row[KeyDataIndex] | typeof NULL_STRING;

			return key === NULL_STRING;
		});

		if (editingRow == null && rowWithNullStringKey != null) {
			const dataWithoutNullStringKey = data.filter((row) => {
				const key = row[keyDataIndex] as Row[KeyDataIndex] | typeof NULL_STRING;

				return key !== NULL_STRING;
			});
			setData(dataWithoutNullStringKey);
		}
	}, [keyDataIndex, data, editingRow]);

	const deleteRow = useCallback(async (record: Row) => {
		try {
			if (onDelete != null) {
				await onDelete(record[keyDataIndex], record);
				notification.success({
					message: 'Successfully deleted',
				});
			}
		} catch (error) {
			console.error('Error deleting table row.', error);
			showErrorNotification('Could not delete item', error as Error);
		}
	}, [keyDataIndex, onDelete]);

	const cancelEditing = useCallback((e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
		if (typeof extraOnCancelEdit === 'function') {
			extraOnCancelEdit();
		}

		if (updateEditingRow != null) {
			updateEditingRow(null);
		}

		setCurrentFormValues({});

		if (e && typeof e.stopPropagation === 'function') {
			e.stopPropagation();
		}
	}, [extraOnCancelEdit, updateEditingRow]);

	const saveEditing = useCallback(async (record: Row, e?: any) => {
		setLoading(true);
		setAddingNew(false);

		e?.stopPropagation();

		try {
			const row = await form.validateFields();

			if (record[keyDataIndex] === NULL_STRING) {
				await onAddNew?.(row);
			} else {
				await onSave(record[keyDataIndex], form.getFieldsValue(true));
			}

			if (updateEditingRow != null) {
				updateEditingRow(null);
			}

			setCurrentFormValues({});
		} catch (error) {
			console.error('Error saving table item.', error);
			showErrorNotification('Could not save item', error as Error);
		} finally {
			setLoading(false);
		}
	}, [form, keyDataIndex, onSave, onAddNew, updateEditingRow]);

	const mergedColumns = useMemo(() => {
		// Helper function to get the merged record with current form values
		const getMergedRecord = (record: Row): Row => {
			if (record[keyDataIndex] === editingRow && Object.keys(currentFormValues).length > 0) {
				return { ...record, ...currentFormValues };
			}

			return record;
		};

		const firstEditableDataIndex = autoFocusOnAdd ?
			(columns.find(
				(col) => checkIsEditable(col, { [keyDataIndex]: NULL_STRING } as Row),
			) as EditableColumn<Row, KeyDataIndex> | undefined)?.dataIndex :
			undefined;

		const merged: any = columns.map((col) => ({
			...col,
			// Add a 4th parameter, the currently edited row id, to column render
			render: col.render != null ?
				(text: any, record: Row, index: number): ((
					| React.ReactNode |
					RenderedCell<Row>
				)) => {
					const mergedRecord = getMergedRecord(record);

					return col?.render?.(text, mergedRecord, index, editingRow);
				} :
				col.render,
			onCell: (record: Row) => {
				const mergedRecord = getMergedRecord(record);

				return {
					save: () => saveEditing(record),
					col,
					editing: (
						record[keyDataIndex] === editingRow &&
						checkIsEditable(col, mergedRecord)
					),
					extra: {
						...((col as any).editingProps ?? {}),
						form,
						saveOnEnter,
						inputProps: {
							...(((col as any).editingProps?.inputProps ?? {}) as any),
							...((
								autoFocusOnAdd &&
								focusNewRow &&
								record[keyDataIndex] === NULL_STRING &&
								(col as any).dataIndex === firstEditableDataIndex
							) ? { autoFocus: true } : {}),
							onKeyDown: (e: React.KeyboardEvent<HTMLElement> & {
								target: HTMLInputElement;
							}) => {
								const type = (col as any).editingProps?.type;

								if (e.key === 'Enter' && (type === 'number' || type === 'currency')) {
									e.preventDefault();

									form.setFieldValue(
										e.target.id,
										Number(stringToValidNumericString(e.target.value)),
									);
								}
							},
						},
					},
				};
			},
		}));

		if (!showActions) {
			return merged;
		}

		merged.push({
			title: addButtonInHeader ? actionsTitle : (
				<AddButton
					onClick={addNew}
					disabled={!allowAddNew || editingRow != null}
					size="small"
				/>
			),
			dataIndex: keyDataIndex as string,
			key: keyDataIndex as string,
			align: 'center',
			// If icon buttons are used, always make room for the icons next to each other
			width: iconButtons ? (getActionsBetweenEditAndDelete != null ? 130 : 100) : undefined,
			render: (text: any, record: Row) => {
				const isEditable = typeof enableEdit === 'function' ? enableEdit(record) : enableEdit;
				const isDeletable = typeof enableDelete === 'function' ? enableDelete(record) : enableDelete;

				const isActionAllowed = typeof allowEditRow === 'function' ? allowEditRow(record) : isEditable;

				const isDisabled = disableEdit(record) || editingRow != null;

				const editTooltip = isDisabled ? 'Item cannot be edited' : undefined;
				const deleteTooltip = isDisabled ? 'Item cannot be deleted' : undefined;

				const resolvedDeleteConfirmTitle = typeof deleteMessage === 'function' ?
					deleteMessage(record) :
					(deleteMessage ?? 'Are you sure you want to delete this item?');

				const actions = editingRow === record[keyDataIndex] ? (
					<>
						<Button
							type="link"
							onClick={(e) => saveEditing(record, e)}
							confirmTitle={confirmTitle}
							icon={iconButtons ? (
								<CheckOutlined
									className={styles.buttonIcon}
								/>
							) : null}
						>
							{!iconButtons && 'Save'}
						</Button>
						<Button
							type="link"
							disabled={loading}
							onClick={cancelEditing}
							icon={iconButtons ? (
								<CloseOutlined
									className={styles.buttonIcon}
								/>
							) : null}
							danger={iconButtons}
						>
							{!iconButtons && 'Cancel'}
						</Button>
					</>
				) : (
					<>
						{isEditable && isActionAllowed && (
							<Button
								type="link"
								disabled={isDisabled}
								onClick={(e) => startEdit(record, e)}
								disabledTooltip={editTooltip}
								icon={
									iconButtons ? (
										<EditOutlined className={styles.buttonIcon} />
									) : null
								}
							>
								{!iconButtons && editLabel}
							</Button>
						)}
						{getActionsBetweenEditAndDelete != null &&
							editingRow == null &&
							getActionsBetweenEditAndDelete(record)}
						{isDeletable && isActionAllowed && (
							<Button
								type="link"
								danger={iconButtons}
								disabled={isDisabled}
								onClick={() => deleteRow(record)}
								confirmTitle={resolvedDeleteConfirmTitle}
								disabledTooltip={deleteTooltip}
								icon={
									iconButtons ? (
										<DeleteOutlined className={styles.buttonIcon} />
									) : null
								}
							>
								{!iconButtons && 'Delete'}
							</Button>
						)}
						{getCustomActions && getCustomActions(record)}
					</>
				);

				return (
					<div className={styles.actionsWrapper}>
						{actions}
					</div>
				);
			},
		});

		return merged;
	}, [
		columns,
		showActions,
		actionsTitle,
		keyDataIndex,
		iconButtons,
		editingRow,
		currentFormValues,
		checkIsEditable,
		form,
		saveEditing,
		enableEdit,
		enableDelete,
		loading,
		cancelEditing,
		editLabel,
		getCustomActions,
		getActionsBetweenEditAndDelete,
		startEdit,
		deleteRow,
		addButtonInHeader,
		deleteMessage,
		confirmTitle,
		autoFocusOnAdd,
		focusNewRow,
	]);

	// This messes with the "No data" cell
	// So only use it if we have at least 1 row shown
	const components = (data != null && data.length > 0) ? {
		body: { cell: EditableCell },
	} : undefined;

	return (
		<Form
			form={form}
			onValuesChange={(changedValues, values) => {
				if (transformers) {
					transformers.forEach((t) => {
						const { field, fn } = t;

						const result = fn(values, changedValues);

						if (result != null){
							form.setFieldValue(field, result);
						}
					});
				}

				if (editingRow != null) {
					const updatedValues = form.getFieldsValue(true);

					setCurrentFormValues(updatedValues);

					if (addingNew) {
						onAddNewChange?.(updatedValues);
					}
				}
			}} 
			component={false}
		>
			{(allowAddNew && addButtonInHeader) && (
				<Space style={{ margin: '4px 0px' }} className={addNewContainerClassName}>
					<Flex gap={5}>
						<Typography.Title level={4} className={styles.addButtonText}>
							{addNewText}
						</Typography.Title>
						<AddButton
							data-testid={addButtonTestId ?? 'add-button'}
							onClick={addNew}
							disabled={editingRow != null}
							size="small"
						/>
					</Flex>
					{addRowExtraContent}
				</Space>
			)}
			<Table
				rowKey={keyDataIndex as string}
				columns={mergedColumns}
				dataSource={data}
				components={{
					...components,
					...props.components,
				}}
				pagination={{
					onChange: () => cancelEditing(),
				}}
				onRowClick={((editingRow == null && onRowClick) ?
					onRowClick :
					undefined
				)}
				rowClassName={(r) => {
					if (typeof rowClassName === 'function') {
						return rowClassName(r);
					}

					return classNames(
						rowClassName,
						{
							[styles.rowClickable]: (
								typeof onRowClick === 'function' &&
								editingRow == null
							),
							...(editingRowClassName != null ? {
								[editingRowClassName]: r[keyDataIndex] === editingRow,
							} : {}),
						},
					);
				}}
				{...props}
			/>
		</Form>
	);
};

export default EditableTable;

